import React, { useState, useCallback, useRef, useEffect } from 'react';
import { 
  Box, 
  Typography, 
  ToggleButton,
  ToggleButtonGroup,
  IconButton,
  Tooltip,
  Button
} from '@mui/material';
import { Undo, Redo, Add, ColorLens, Delete, BorderVertical, TableRows, FileUpload, FileDownload } from '@mui/icons-material';
import ChartGrid from './ChartGrid';
import STITCH_TYPES from './stitchTypes';
import InstructionsPanel from './InstructionsPanel';
import KeyPanel from './KeyPanel';
import ColorPalette from './ColorPalette';
import StitchPalette from './StitchPalette';
import RowColControls from './RowColControls';

const INITIAL_ROWS = 10;
const INITIAL_COLS = 10;

// Default stitch for new/cleared cells
const DEFAULT_STITCH = 'k';

// Dynamically build categories from STITCH_TYPES
const getStitchCategories = () => {
  const categories = {};
  Object.entries(STITCH_TYPES).forEach(([key, stitch]) => {
    if (!stitch.category) return;
    if (!categories[stitch.category]) categories[stitch.category] = [];
    categories[stitch.category].push(key);
  });
  return categories;
};

const getGridSizeFromData = (knitdata, colordata) => {
  // If both arrays are missing/empty, use defaults
  if ((!Array.isArray(knitdata) || knitdata.length === 0) && (!Array.isArray(colordata) || colordata.length === 0)) {
    return { rows: INITIAL_ROWS, cols: INITIAL_COLS };
  }
  // If only knitdata is present
  if (Array.isArray(knitdata) && knitdata.length > 0) {
    const rows = knitdata.length;
    const cols = Math.max(0, ...knitdata.map(r => Array.isArray(r) ? r.length : 0));
    return { rows, cols };
  }
  // If only colordata is present
  if (Array.isArray(colordata) && colordata.length > 0) {
    const rows = colordata.length;
    const cols = Math.max(0, ...colordata.map(r => Array.isArray(r) ? r.length : 0));
    return { rows, cols };
  }
  // Fallback to defaults
  return { rows: INITIAL_ROWS, cols: INITIAL_COLS };
};

const ensureGridSize = (arr, rows, cols, fill) => {
  if (!Array.isArray(arr)) arr = [];
  const newArr = [];
  for (let i = 0; i < rows; i++) {
    const row = Array.isArray(arr[i]) ? arr[i].slice(0, cols) : [];
    while (row.length < cols) row.push(fill);
    newArr.push(row);
  }
  return newArr;
};

const ChartEditor = (props) => {
  // Determine grid size from input arrays
  const { rows: initialRows, cols: initialCols } = getGridSizeFromData(props.knitdata, props.colordata);

  // State declarations
  const [knitdata, setKnitdata] = useState(() =>
    ensureGridSize(props.knitdata, initialRows, initialCols, DEFAULT_STITCH)
  );
  const [colors, setColors] = useState(() => props.colors || [
    '#ffffff', // 0: white (default)
    '#000000', // 1: black
    '#e57373', // 2: red
    '#64b5f6', // 3: blue
    '#81c784', // 4: green
    '#ffd54f', // 5: yellow
    '#ba68c8', // 6: purple
    '#ffb74d', // 7: orange
    '#a1887f', // 8: brown
    '#90a4ae', // 9: gray
  ]);
  const [icons, setIcons] = useState(() => props.icons || Array(10).fill(''));
  const [colordata, setColordata] = useState(() =>
    ensureGridSize(props.colordata, initialRows, initialCols, null)
  ); // placeholder for color layer
  const [verticalLines, setVerticalLines] = useState(() => props.verticalLines || []); // array of col indices
  const [selectedStitch, setSelectedStitch] = useState('k');
  const [history, setHistory] = useState([JSON.stringify(knitdata)]);
  const [historyIndex, setHistoryIndex] = useState(0);
  const [startWithRS, setStartWithRS] = useState(true);
  const [activeCategory, setActiveCategory] = useState('Basic');
  const [showVerticalLineControls, setShowVerticalLineControls] = useState(false);
  const [cellSize, setCellSize] = useState(16);
  const [importError, setImportError] = useState('');
  const [selectedColorIdx, setSelectedColorIdx] = useState(0);
  const fileInputRef = useRef();
  const [mode, setMode] = useState('stitch'); // 'stitch', 'color', 'lines', 'clear', 'rowcol'
  
  // Use categories from stitchTypes
  const STITCH_CATEGORIES = getStitchCategories();
  
  // Update knitdata and history
  const updateKnitdata = useCallback((newKnitdata) => {
    const newHistory = history.slice(0, historyIndex + 1);
    newHistory.push(JSON.stringify(newKnitdata));
    setHistory(newHistory);
    setHistoryIndex(newHistory.length - 1);
    setKnitdata(newKnitdata);
  }, [history, historyIndex]);
  
  // Undo/Redo handlers
  const handleUndo = useCallback(() => {
    if (historyIndex > 0) {
      const prevIndex = historyIndex - 1;
      setKnitdata(JSON.parse(history[prevIndex]));
      setHistoryIndex(prevIndex);
    }
  }, [history, historyIndex]);
  
  const handleRedo = useCallback(() => {
    if (historyIndex < history.length - 1) {
      const nextIndex = historyIndex + 1;
      setKnitdata(JSON.parse(history[nextIndex]));
      setHistoryIndex(nextIndex);
    }
  }, [history, historyIndex]);
  
  // Convert knitdata to text instructions
  const knitdataToInstructions = () => {
    return knitdata.map((row, i) => {
      const rowNum = i + 1;
      if (row.every(stitch => !stitch)) return `Row ${rowNum}: (empty)`;
      
      const stitches = [];
      let currentStitch = row[0] || 'k';
      let count = 1;

      for (let j = 1; j < row.length; j++) {
        const stitch = row[j] || 'k';
        if (stitch === currentStitch) {
          count++;
        } else {
          stitches.push(count > 1 ? `${currentStitch}${count}` : currentStitch);
          currentStitch = stitch;
          count = 1;
        }
      }
      stitches.push(count > 1 ? `${currentStitch}${count}` : currentStitch);

      return `Row ${rowNum}: ${stitches.join(', ')}`;
    }).join('\n');
  };
  
  // Rest of the component code...
  const handleStitchClick = useCallback((row, col) => {
    // Compute new grid based on current knitdata
    const rows = knitdata.length;
    const cols = knitdata[0]?.length || initialCols;
    let next = ensureGridSize(knitdata, rows, cols, DEFAULT_STITCH);
    if (mode === 'clear') {
      const stitch = next[row][col];
      if (!stitch || stitch === DEFAULT_STITCH) return;
      // Replace with default stitch
      next[row][col] = DEFAULT_STITCH;
      // If it's a multi-cell stitch, clean up the subsequent cells
      const stitchWidth = STITCH_TYPES[stitch]?.width || 1;
      for (let i = 1; i < stitchWidth; i++) {
        if (col + i < cols) {
          next[row][col + i] = DEFAULT_STITCH;
        }
      }
    } else if (selectedStitch) {
      const newStitchWidth = STITCH_TYPES[selectedStitch]?.width || 1;
      // Prevent placing wide stitch if it would overflow
      if (col + newStitchWidth > cols) return;
      // --- Overlap clearing logic ---
      for (let i = 0; i < newStitchWidth; i++) {
        const c = col + i;
        const cell = next[row][c];
        if (cell === null || (cell && cell !== DEFAULT_STITCH && STITCH_TYPES[cell]?.width > 1)) {
          // Find the start of the wide stitch
          let start = c;
          while (start > 0 && next[row][start] === null) start--;
          const wideStitch = next[row][start];
          const wideStitchWidth = STITCH_TYPES[wideStitch]?.width || 1;
          // Clear the entire wide stitch
          for (let j = 0; j < wideStitchWidth; j++) {
            if (start + j < cols) {
              next[row][start + j] = DEFAULT_STITCH;
            }
          }
        }
      }
      // Place the new stitch
      next[row][col] = selectedStitch;
      // If it's a multi-cell stitch, mark the next cells as null
      for (let i = 1; i < newStitchWidth; i++) {
        if (col + i < cols) {
          next[row][col + i] = null; // Mark as part of multi-cell stitch
        }
      }
    }
    // Ensure the affected row is exactly the correct length
    if (next[row].length < cols) {
      while (next[row].length < cols) next[row].push(DEFAULT_STITCH);
    } else if (next[row].length > cols) {
      next[row] = next[row].slice(0, cols);
    }
    // Ensure all rows are correct length
    next = ensureGridSize(next, rows, cols, DEFAULT_STITCH);
    // Use updateKnitdata so history is updated
    updateKnitdata(next);
  }, [knitdata, mode, selectedStitch, initialCols, updateKnitdata]);

  const handleClearCell = useCallback((row, col) => {
    const newKnitdata = knitdata.map(r => [...r]);
    const stitch = newKnitdata[row][col];
    if (!stitch || stitch === DEFAULT_STITCH) return;
    // Replace with default stitch
    newKnitdata[row][col] = DEFAULT_STITCH;
    // If it's a multi-cell stitch, clean up the subsequent cells
    const stitchWidth = STITCH_TYPES[stitch]?.width || 1;
    for (let i = 1; i < stitchWidth; i++) {
      if (col + i < newKnitdata[0].length) {
        newKnitdata[row][col + i] = DEFAULT_STITCH;
      }
    }
    updateKnitdata(newKnitdata);
  }, [knitdata, updateKnitdata]);

  // Handler to toggle vertical line at a column
  const handleToggleVerticalLine = (colIdx) => {
    setVerticalLines((prev) =>
      prev.includes(colIdx)
        ? prev.filter((c) => c !== colIdx)
        : [...prev, colIdx].sort((a, b) => a - b)
    );
  };

  // Export chart data as JSON file
  const handleExportChart = () => {
    const data = JSON.stringify({
      knitdata,
      verticalLines,
      colordata,
      colors,
      icons
    }, null, 2);
    const blob = new Blob([data], { type: 'application/json' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = 'knit-chart.json';
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    URL.revokeObjectURL(url);
  };

  // Import chart data from JSON file
  const handleImportChart = (e) => {
    setImportError('');
    const file = e.target.files[0];
    if (!file) return;
    const reader = new FileReader();
    reader.onload = (event) => {
      try {
        const data = JSON.parse(event.target.result);
        if (Array.isArray(data.knitdata) && Array.isArray(data.verticalLines)) {
          setKnitdata(data.knitdata);
          setVerticalLines(data.verticalLines);
          setHistory([JSON.stringify(data.knitdata)]);
          setHistoryIndex(0);
          if (Array.isArray(data.colordata)) {
            setColordata(data.colordata);
          }
          if (Array.isArray(data.colors)) {
            setColors(data.colors);
          }
          if (Array.isArray(data.icons)) {
            setIcons(data.icons);
          }
        } else {
          setImportError('This file could not be loaded. Please make sure you are importing a valid chart file exported from this app.');
        }
      } catch (err) {
        setImportError('Failed to import chart: ' + err.message);
      }
    };
    reader.readAsText(file);
    // Reset input so same file can be re-imported
    e.target.value = '';
  };

  // When grid size changes, ensure knitdata/colordata are correct size
  useEffect(() => {
    setKnitdata(kd => ensureGridSize(kd, initialRows, initialCols, DEFAULT_STITCH));
    setColordata(cd => ensureGridSize(cd, initialRows, initialCols, null));
  }, [initialRows, initialCols]);

  // When user adds a color or symbol, ensure arrays are initialized
  const handleColorCell = useCallback((row, col) => {
    setColordata(cd => {
      const rows = knitdata.length;
      const cols = knitdata[0]?.length || initialCols;
      const next = ensureGridSize(cd, rows, cols, null);
      next[row][col] = selectedColorIdx;
      return next;
    });
  }, [knitdata, selectedColorIdx, initialCols]);

  const handleModeChange = (value) => {
    if (!value) return;
    setMode(value);
    if (value === 'lines') {
      setShowVerticalLineControls(v => !v);
    } else {
      setShowVerticalLineControls(false);
    }
  };

  // Save handler
  const handleSave = () => {
    if (props.onSave) {
      props.onSave({
        knitdata,
        verticalLines,
        colordata,
        colors,
        icons
      });
    }
  };

  // Cancel handler
  const handleCancel = () => {
    if (props.onCancel) {
      props.onCancel();
    }
  };

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', height: '100%', minHeight: 600 }}>
      {/* Controls and Palette above chart */}
      <Box sx={{ width: '100%', display: 'flex', flexDirection: 'column', p: 2, pb: 0 }}>
        {/* Chart Editing Toolbar */}
        {importError && (
          <Box sx={{ my: 1, p: 1, bgcolor: '#fff3f3', color: 'error.main', border: '1px solid', borderColor: 'error.light', borderRadius: 1 }}>
            <Typography variant="body2">{importError}</Typography>
          </Box>
        )}
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2, flexWrap: 'wrap' }}>
          <ToggleButtonGroup
            exclusive
            value={mode}
            onChange={(_, value) => handleModeChange(value)}
            sx={{ mb: 0, gap: 1 }}
          >
            <ToggleButton value="stitch" size="small" sx={{ fontWeight: 'bold', px: 2 }}>
              <Add fontSize="small" sx={{ mr: 1 }} />Stitch
            </ToggleButton>
            <ToggleButton value="color" size="small" sx={{ fontWeight: 'bold', px: 2 }}>
              <ColorLens fontSize="small" sx={{ mr: 1 }} />Color
            </ToggleButton>
            <ToggleButton value="lines" size="small" sx={{ fontWeight: 'bold', px: 2 }}>
              <BorderVertical fontSize="small" sx={{ mr: 1 }} />Lines
            </ToggleButton>
            <ToggleButton value="clear" size="small" sx={{ fontWeight: 'bold', px: 2 }}>
              <Delete fontSize="small" sx={{ mr: 1 }} />Clear
            </ToggleButton>
            <ToggleButton value="rowcol" size="small" sx={{ fontWeight: 'bold', px: 2 }}>
              <TableRows fontSize="small" sx={{ mr: 1 }} />Rows/Cols
            </ToggleButton>
          </ToggleButtonGroup>
          <Tooltip title="Undo">
            <span>
              <IconButton onClick={handleUndo} disabled={historyIndex === 0} size="small">
                <Undo fontSize="small" />
              </IconButton>
            </span>
          </Tooltip>
          <Tooltip title="Redo">
            <span>
              <IconButton onClick={handleRedo} disabled={historyIndex === history.length - 1} size="small">
                <Redo fontSize="small" />
              </IconButton>
            </span>
          </Tooltip>
          <Tooltip title="Export Chart">
            <IconButton onClick={handleExportChart} size="small">
              <FileDownload fontSize="small" />
            </IconButton>
          </Tooltip>
          <Tooltip title="Import Chart">
            <span>
              <IconButton onClick={() => fileInputRef.current && fileInputRef.current.click()} size="small">
                <FileUpload fontSize="small" />
              </IconButton>
            </span>
          </Tooltip>
          <input
            type="file"
            accept="application/json"
            ref={fileInputRef}
            style={{ display: 'none' }}
            onChange={handleImportChart}
          />
        </Box>
        {/* Row/Col controls */}
        {mode === 'rowcol' && (
          <RowColControls
            knitdata={knitdata}
            setKnitdata={setKnitdata}
            history={history}
            setHistory={setHistory}
            historyIndex={historyIndex}
            setHistoryIndex={setHistoryIndex}
            cellSize={cellSize}
            setCellSize={setCellSize}
            startWithRS={startWithRS}
            setStartWithRS={setStartWithRS}
          />
        )}
        {/* Stitch Palette - only show if Add Stitch is selected */}
        {mode === 'stitch' && (
          <StitchPalette
            STITCH_TYPES={STITCH_TYPES}
            STITCH_CATEGORIES={STITCH_CATEGORIES}
            activeCategory={activeCategory}
            setActiveCategory={setActiveCategory}
            selectedStitch={selectedStitch}
            setSelectedStitch={setSelectedStitch}
            cellSize={cellSize}
          />
        )}
        {mode === 'color' && (
          <ColorPalette
            colors={colors}
            icons={icons}
            setIcons={setIcons}
            selectedColorIdx={selectedColorIdx}
            setSelectedColorIdx={setSelectedColorIdx}
            setColors={setColors}
          />
        )}
      </Box>

      <Box sx={{ display: 'flex', flexDirection: 'row', gap: 4 }}>
          <ChartGrid 
            knitdata={knitdata}
            colordata={colordata} // pass color indices only
            colors={colors} // pass palette
            icons={icons} // pass palette
            verticalLines={verticalLines}
            onStitchClick={mode === 'color' ? handleColorCell : handleStitchClick}
            onClearCell={handleClearCell}
            cellSize={cellSize}
            startWithRS={startWithRS}
            deleteMode={mode === 'clear'}
            onToggleVerticalLine={showVerticalLineControls ? handleToggleVerticalLine : undefined}
            selectedStitch={selectedStitch}
            mode={mode}
          />
        <KeyPanel grid={knitdata} cellSize={cellSize} />
      </Box>
      <InstructionsPanel instructions={knitdataToInstructions()} />
      <Box sx={{ display: 'flex', justifyContent: 'flex-end', gap: 2, mt: 2 }}>
        <Button onClick={handleSave} variant="contained" color="primary">Save</Button>
        <Button onClick={handleCancel} variant="outlined">Cancel</Button>
      </Box>
    </Box>
  );
};

export default ChartEditor;
